import React, { useMemo } from "react";
import { Trash2, Pencil, Info, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader } from "lucide-react";

export default function ConversionRulesTable({
  data = [],
  loading = false,
  error = null,
  currentPage = 1,
  itemsPerPage = 10,
  totalEntries = 0,
  startEntry = 0,
  endEntry = 0,
  maxPages = 1,
  onDelete = () => {},
  onEdit = () => {},
  onInfo = () => {},
  onToggleStatus = () => {},
  onPageChange = () => {},
  onItemsPerPageChange = () => {},
  existingVariants = [],
  baseProduct = null,
  loadingEditId = null,
}) {
  const formatCurrency = (val) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(val || 0);

  // Group rules by product_id + from_unit_id so multi-variant products
  // show as one row with per-variant prices instead of duplicate rows.
  const groupedData = useMemo(() => {
    const groups = {};
    data.forEach((rule) => {
      const key = `${rule.product_id}__${rule.from_unit_id}`;
      if (!groups[key]) {
        groups[key] = {
          ...rule,          // spread first rule for common fields
          variantPrices: [],
          ruleIds: [],      // all rule IDs in this group
          total_stock: 0,
        };
      }
      groups[key].ruleIds.push(rule.id);
      groups[key].total_stock += Number(rule.current_stock || 0);
      
      if (rule.variant_id) {
        const alreadyAdded = groups[key].variantPrices.some(
          (vp) => vp.variant_id === rule.variant_id
        );
        if (!alreadyAdded) {
          const vName = rule.variant_name || rule.variant?.name || rule.Variant?.name || rule.variant?.Name;
          groups[key].variantPrices.push({
            variant_id: rule.variant_id,
            variant_name: vName || "Varian",
            sell_price: rule.sell_price,
          });
        }
      }
    });
    return Object.values(groups);
  }, [data]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm mt-6 overflow-hidden">
        <div className="px-6 py-12 flex items-center justify-center text-slate-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm mt-6 overflow-hidden">
        <div className="px-6 py-4 bg-rose-50 border-b border-rose-200 text-rose-700">
          <p className="font-medium">Terjadi Kesalahan</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm mt-6 overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="px-6 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Aksi
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Nama Produk
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Satuan Dasar
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Satuan Konversi
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Nilai Konversi
              </th>
              {/* HIDE FOR NOW: Kolom Harga Jual pada tabel konversi stok
              <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Harga Jual
              </th>
              */}
              <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Stok Tersedia
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {groupedData.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-slate-400 text-sm">
                  Tidak ada aturan konversi
                </td>
              </tr>
            ) : (
              groupedData.map((item) => {
                const isLoadingThis = item.ruleIds.some((id) => id === loadingEditId);

                return (
                  <tr
                    key={`${item.product_id}_${item.from_unit_id}`}
                    className="hover:bg-slate-50/70 transition-colors"
                  >
                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => onEdit(item)}
                          disabled={isLoadingThis}
                          className="w-8 h-8 bg-slate-800 text-white rounded-lg hover:bg-slate-700 flex items-center justify-center transition-colors shadow-sm disabled:opacity-75"
                          title="Edit"
                        >
                          {isLoadingThis ? (
                            <svg
                              className="animate-spin h-3.5 w-3.5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          ) : (
                            <Pencil className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => onDelete(item.ruleIds)}
                          className="w-8 h-8 bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex items-center justify-center transition-colors shadow-sm"
                          title="Hapus"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onInfo(item)}
                          className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors shadow-sm"
                          title="Detail"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </div>
                    </td>

                    {/* Product name */}
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      {item.product_name || baseProduct?.name || "-"}
                    </td>

                    {/* Satuan Dasar (to_unit) */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {item.to_unit_name || baseProduct?.unit_name || "-"}
                    </td>

                    {/* Satuan Konversi (from_unit) */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-medium">
                      {item.from_unit_name || "-"}
                    </td>

                    {/* Nilai Konversi */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      1 {item.from_unit_name} = {item.conversion_factor || 0}{" "}
                      {item.to_unit_name || baseProduct?.unit_name}
                    </td>

                    {/* HIDE FOR NOW: Kolom Harga Jual pada tabel konversi stok
                    <td className="px-6 py-4 text-sm text-slate-900">
                      {item.variantPrices.length > 0 ? (
                        <div className="space-y-1">
                          {item.variantPrices.map((vp) => (
                            <div
                              key={vp.variant_id}
                              className="flex items-center gap-1.5"
                            >
                              <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-md border border-slate-200 leading-none">
                                {vp.variant_name}
                              </span>
                              <span className="font-medium whitespace-nowrap">
                                {formatCurrency(vp.sell_price)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="whitespace-nowrap">
                          {formatCurrency(item.sell_price)} /{" "}
                          {item.from_unit_name}
                        </span>
                      )}
                    </td>
                    */}

                    {/* Stok */}
                    <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap">
                      {item.total_stock} {item.to_unit_name}
                      {item.conversion_factor
                        ? ` (${Math.floor(
                            item.total_stock / item.conversion_factor
                          )} ${item.from_unit_name})`
                        : ""}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-slate-50/40">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Tampilkan</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="px-2 py-1.5 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>
              Menampilkan {startEntry} - {endEntry} dari Total {totalEntries}{" "}
              Data
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage <= 1}
              className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
              title="Halaman pertama"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
              title="Halaman sebelumnya"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 rounded-lg bg-teal-600 text-white text-sm font-semibold min-w-10 text-center shadow-sm shadow-teal-600/20">
              {currentPage}
            </span>
            <button
              onClick={() => onPageChange(Math.min(maxPages, currentPage + 1))}
              disabled={currentPage >= maxPages}
              className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
              title="Halaman berikutnya"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange(maxPages)}
              disabled={currentPage >= maxPages}
              className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
              title="Halaman terakhir"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
